import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Box, ButtonBase, Drawer } from "@mui/material";
import { useMemo, useState } from "react";
import Flex from "../../../../components/Flex";
import CustomTabs from "../../../../components/app/CustomTabs";
import LocationsDrawer from "../../../../components/app/admin/provider-groups/LocationsDrawer";
import { ProviderGroupLocationFormType } from "../../../../constants/ProviderLocationFromInitialValues";
import { appPaths } from "../../../../constants/paths";
import useDisclosure from "../../../../hooks/use-disclosure";
import LocationDetails from "./LocationDetails";
import LocationDepartment from "./LocationDepartment";
import LocationPatient from "./LocationPatient";
import LocationStaffUser from "./LocationUser";
import ButtonWithSelect from "../../../../components/ButtonWithSelect";
import { useProviderGroupLocationProfile } from "../../../../queries/admin/provider-group/use-provider-group-location-profile";
import { useParams } from "react-router-dom";
import { useDepartmentList } from "../../../../queries/use-get-departments";
import { useProviderGroupLocationUpdate } from "../../../../queries/admin/provider-group/use-pg-location-update";
import { useUsersList } from "../../../../queries/use-get-users";
import { useDebounce } from "../../../../hooks/use-debounce";
import { useLinkLocationDepartment } from "../../../../queries/use-location-department-link";
import {
  DEF_PAGINATION,
  DEFAULT_UUID_NAME_LIST,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
  unNecessaryFEOfficeHourTrans,
} from "../../../../utils/utils";
import { useAssignLocationToUser } from "../../../../queries/use-link-user";
import { useQueryClient } from "@tanstack/react-query";
import LocationProviderUser from "./LocationProviders";
import { useProvidersList } from "../../../../queries/provider/setting/use-providers-list";
import { useAssignProviderToLocation } from "../../../../queries/use-assign-provider-to-location";
import CustomTitle from "../../../../components/app/CustomTitle";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";
// import { useProviderGroupProfile } from "../../../../queries/admin/provider-group/use-provider-group-profile";

type LocationDataType = {
  value: string;
  path: string;
  label: string;
};

const LOCATION_FEATURE: LocationDataType[] = [
  {
    label: "Details",
    value: "Details",
    path: appPaths.providerLocationDetails,
  },
  {
    label: "Departments",
    value: "Departments",
    path: appPaths.providerLocationDepartment,
  },
  {
    label: "Patients",
    value: "patients",
    path: appPaths.providerLocationPatient,
  },
  {
    label: "Staffs",
    value: "staffs",
    path: appPaths.providerLocationUser,
  },
  {
    label: "Providers",
    value: "providers",
    path: appPaths.providerLocationUser,
  },
];

interface ProviderLocationIdLayoutProps {
  openedMainLocationModal: boolean;
  onClose: () => void;
  locationId?: string;
  groupNpiNumber?: string;
  pgName?: string;
  providerSpecialities?: { uuid: string; name: string };
}

const ProviderLocationIdLayoutModal = ({
  openedMainLocationModal,
  onClose,
  locationId,
  groupNpiNumber,
  pgName,
  providerSpecialities,
}: ProviderLocationIdLayoutProps) => {
  const { providerGroupId } = useParams();
  const { data: locationProfile, refetch } = useProviderGroupLocationProfile({
    locationId: locationId!,
    xTENANTID: providerGroupId,
  });

  const { mutateAsync: updateLocation } = useProviderGroupLocationUpdate();
  const { mutateAsync: linkDepartment } = useLinkLocationDepartment();
  const { mutateAsync: linkUser } = useAssignLocationToUser();
  const { mutateAsync: linkProvider } = useAssignProviderToLocation();

  const [activeButton, setActiveButton] = useState<string>("Details");
  /** @note use a new toast for showing another error
   * Because it use for ButtonWithSelect that while this toast is null
   * show toast at the place of ButtonWithSelect
   */
  const showSnackbar = useSnackbar();
  const { opened, open, close } = useDisclosure(false);
  const params = useParams();
  const [searchKey, setSearchKey, instantSearchVal] = useDebounce<string>("");
  const client = useQueryClient();
  const viewAs = getViewAs();
  const realm = getStoredRealm();
  const xTENANTID =
    params?.providerGroupId ?? (viewAs === "provider" ? realm : undefined);

  const { data: RawUsersList } = useUsersList({
    statusKey: true,
    pagination: DEF_PAGINATION,
    searchKey: searchKey,
    providerGroupId: xTENANTID ?? undefined,
  });

  const { data: RawProvidersList } = useProvidersList({
    status: true,
    pagination: DEF_PAGINATION,
    searchKey,
    xTENANTID: xTENANTID ?? undefined,
  });

  const { data: RawLocationProvidersList } = useProvidersList({
    status: true,
    locationUuid: locationId,
    pagination: DEF_PAGINATION,
    xTENANTID: xTENANTID ?? "",
  });

  const { data: RawLocationsDepartmentList } = useDepartmentList({
    pagination: DEF_PAGINATION,
    status: true,
    searchKey,
    providerGroupId: xTENANTID,
    locationUuid: locationId,
  });

  const { data: RawDepartmentList } = useDepartmentList({
    status: true,
    pagination: DEF_PAGINATION,
    searchKey,
    providerGroupId: xTENANTID,
  });

  const { data: RawLocationUsersList } = useUsersList({
    statusKey: true,
    locationUuid: locationId,
    providerGroupId: xTENANTID ?? undefined,
    pagination: DEF_PAGINATION,
  });

  const filteredDepartments = useMemo(() => {
    const locationDepartmentIds = new Set(
      RawLocationsDepartmentList?.data?.content?.map(
        (provider) => provider.uuid
      )
    );
    return RawDepartmentList?.data?.content?.filter(
      (provider) => !locationDepartmentIds.has(provider.uuid)
    );
  }, [RawLocationsDepartmentList, RawDepartmentList]);

  const filteredStaffUsers = useMemo(() => {
    const locationStaffIds = new Set(
      RawLocationUsersList?.data?.content?.map((provider) => provider.uuid)
    );
    return RawUsersList?.data?.content?.filter(
      (provider) => !locationStaffIds.has(provider.uuid)
    );
  }, [RawLocationUsersList, RawUsersList]);

  const filteredProviders = useMemo(() => {
    const locationProviderIds = new Set(
      RawLocationProvidersList?.data?.content?.map((provider) => provider.uuid)
    );
    return RawProvidersList?.data?.content?.filter(
      (provider) => !locationProviderIds.has(provider.uuid)
    );
  }, [RawLocationProvidersList, RawProvidersList]);

  function editLocation(values: ProviderGroupLocationFormType) {
    const transFormData: any = getUnNecessaryTransformData(
      ["specialities"],
      values
    );
    return updateLocation({
      location: transFormData as any,
      xTENANTID: providerGroupId,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  const ACTIVE_LOCATION_COMPONENT: Record<string, JSX.Element> = {
    Details: (
      <LocationDetails
        locationProfile={
          {
            ...locationProfile,
            groupNpiNumber: groupNpiNumber ?? "",
          } as any
        }
      />
    ),
    Departments: (
      <LocationDepartment
        searchKey={searchKey}
        locationUuid={locationProfile?.uuid}
      />
    ),
    patients: <LocationPatient locationUuid={locationId!} />,
    staffs: (
      <LocationStaffUser
        userUuid={locationProfile?.uuid}
        searchKey={searchKey}
        xTENANTID={xTENANTID}
      />
    ),
    providers: (
      <LocationProviderUser
        locationId={locationId}
        searchKey={searchKey}
        xTENANTID={xTENANTID}
      />
    ),
  };

  function addDepartment(depId: string) {
    linkDepartment({
      locationId: locationId ?? "",
      departmentId: depId,
      xTENANTID: xTENANTID ?? undefined,
    }).then(() => {
      refetch();
      client.invalidateQueries({
        queryKey: ["provider-group-departments-list"],
      });
      showSnackbar({ type: "success", message: "Added Successfully" });
    });
  }

  function addStaffUser(value: any) {
    linkUser({
      userId: value,
      locationId: locationId ?? "",
      xTENANTID: providerGroupId,
    }).then(() => {
      refetch();
      client.invalidateQueries({ queryKey: ["provider-group-users-list"] });
      showSnackbar({ type: "success", message: "Added Successfully" });
    });
  }

  function addProviderUser(value: any) {
    linkProvider({
      providerId: value,
      locationId: locationId ?? "",
      xTENANTID: providerGroupId,
    }).then(() => {
      refetch();
      client.invalidateQueries({ queryKey: ["providers-List"] });
      showSnackbar({ type: "success", message: "Added Successfully" });
    });
  }

  return (
    <Drawer
      open={openedMainLocationModal}
      anchor="right"
      PaperProps={{ sx: { width: "74vw" } }}
    >
      <Flex
        justifyContent={"space-between"}
        height={"6.8rem"}
        flexWrap={"wrap"}
        flexDirection={"column"}
        px={"15px"}
      >
        <Flex
          mt={"15px"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <ButtonBase
            onClick={() => {
              onClose();
              setActiveButton("Details");
            }}
          >
            <Flex gap={"10px"} height={"35px"} alignItems={"center"}>
              <ArrowBackIcon sx={{ color: "primary.main" }} fontSize="medium" />
              <CustomTitle color={"primary.main"}>Location</CustomTitle>
            </Flex>
          </ButtonBase>

          <Box>
            {activeButton === "Details" && (
              <StyledButtonV2
                variant="outlined"
                label="Edit Profile"
                onClick={open}
                startIcon={<ModeEditOutlineOutlinedIcon />}
              />
            )}

            {activeButton === "Departments" && (
              <ButtonWithSelect
                w={"220px"}
                label="Link New Department"
                value={instantSearchVal}
                onSearch={(_, v) => setSearchKey(v)}
                options={
                  filteredDepartments?.map((it) => ({
                    value: it?.uuid,
                    label: it?.name,
                  })) ?? []
                }
                onChange={(id) => id && addDepartment(id)}
              />
            )}

            {activeButton === "staffs" && (
              <ButtonWithSelect
                w={"174px"}
                label="Link New User"
                value={instantSearchVal}
                onSearch={(_, v) => setSearchKey(v)}
                options={
                  filteredStaffUsers?.map((it) => ({
                    value: it?.uuid,
                    label: it?.firstName + " " + it?.lastName,
                  })) ?? []
                }
                onChange={addStaffUser}
              />
            )}
            {activeButton === "providers" && (
              <ButtonWithSelect
                w={"198px"}
                label="Link New Provider"
                value={instantSearchVal}
                onSearch={(_, v) => setSearchKey(v)}
                options={
                  filteredProviders?.map((it) => ({
                    value: it?.uuid,
                    label: it?.firstName + " " + it?.lastName,
                  })) ?? []
                }
                onChange={addProviderUser}
              />
            )}
          </Box>
        </Flex>
        <Flex>
          <CustomTabs
            tabsData={LOCATION_FEATURE}
            value={activeButton}
            onChange={(v) => setActiveButton(v)}
          />
        </Flex>
      </Flex>
      <Box marginTop={"10px"} px={"15px"}>
        <LocationsDrawer
          providerGroupTimezone={""}
          open={opened}
          handleCloseModal={close}
          title={`Edit Location For ${pgName}`}
          onSubmit={editLocation}
          shouldGetPrefillList
          prefillSpecialtyList={transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            providerSpecialities
          )}
          preFillValues={
            {
              ...(locationProfile ?? {}),
              officeHours: unNecessaryFEOfficeHourTrans(
                locationProfile?.officeHours as any
              ),
              specialities: transformMultiSelectFrontEndVal(
                DEFAULT_UUID_NAME_LIST,
                locationProfile?.specialities
              ),
            } as any
          }
        />
        {/* render active component */}
        {ACTIVE_LOCATION_COMPONENT[activeButton]}
      </Box>
    </Drawer>
  );
};

export default ProviderLocationIdLayoutModal;
